<script setup lang="ts">
	import { Chart as ChartJS, ArcElement, Tooltip, Legend, type ChartConfiguration, type ChartData } from 'chart.js'
	import { Doughnut } from 'vue-chartjs'

	const isChartMounted = ref(true)

	export type ChartDataContext = ChartConfiguration<'doughnut'>['data']
	export type ChartOptions = ChartConfiguration<'doughnut'>['options']
	export type DoughnutChartContext = ChartData<'doughnut'>

	const props = withDefaults(
		defineProps<{
			chartData: ChartDataContext
			chartOptions?: ChartOptions
			total?: number | string
			description?: string
			useLabels?: boolean
			fillColor?: string
			labelClass?: string
		}>(),
		{
			chartOptions: () => ({
				cutout: '75%',
				spacing: -8,
				borderWidth: 0,
				barThickness: 1,
				responsive: true,
				plugins: {
					legend: {
						display: false
					}
				}
			}),
			useLabels: true,
			labelClass: 'mt-5 grid-cols-2',
			fillColor: '#1D2939'
		}
	)
	const { internationalNoFormat } = useCurrency()
	const registerChart = () => {
		ChartJS.register(
			{
				id: 'custom-dough',
				beforeDatasetsDraw(chart) {
					const { ctx } = chart
					ctx.save()
					const n = chart.getDatasetMeta(0).data ? chart.getDatasetMeta(0).data[0]?.x : 0

					ctx.font = '500 14px Metropolis'
					ctx.fillStyle = props.fillColor
					ctx.textAlign = 'center'
					ctx.textBaseline = 'middle'

					ctx.fillText(String(internationalNoFormat(props.total || 0)), n, 60)
					ctx.fillText(String(props.description), n, 80)
				}
			},
			ArcElement,
			Tooltip,
			Legend
		)
	}
	const emit = defineEmits(['selectedChart'])
	onBeforeMount(() => {
		ChartJS.unregister({
			id: 'custom-dough'
		})
		registerChart()
	})

	// This Method simply unMounts and mounts the chart out of the dom due to the chart not being able to auto update when page changes
	onBeforeRouteLeave(() => {
		ChartJS.unregister({
			id: 'custom-dough'
		})
	})
</script>

<template>
	<div>
		<client-only>
			<div v-if="isChartMounted" class="relative flex h-[140px] flex-col items-center justify-start">
				<doughnut :data="chartData" :options="chartOptions" class="relative z-10 mr-auto" />
			</div>

			<div v-if="useLabels" class="mt-10 flex w-full flex-wrap items-end gap-5" :class="labelClass">
				<charts-label
					v-for="(item, index) in chartData.labels"
					:key="index"
					class="flex cursor-pointer flex-col"
					:title="String(item)"
					:background-color="(chartData.datasets[0].backgroundColor as [])[index] || ''"
					@click="emit('selectedChart', item)"
				/>
				<slot name="labels" :contents="chartData.datasets[0].data" />
			</div>
		</client-only>
	</div>
</template>
